import { useEffect, useRef, useState } from "react";
import { Mic, Loader2, PhoneOff } from "lucide-react";
import { toast } from "sonner";
import { saveCartesiaTranscript } from "../lib/api";

/**
 * Telefonata vocale via Voice Agent (interno 24Vox).
 * Protocollo Calls API:
 *  1. Backend mint access token (grants: agent) + ritorna agent_id, voice_id, version
 *  2. Frontend apre WSS wss://api.cartesia.ai/agents/stream/{agent_id}?access_token=...&cartesia_version=...
 *  3. Primo evento: { event:"start", config:{input_format:"pcm_44100", voice_id}, agent:{introduction, system_prompt}, metadata }
 *  4. Server risponde con { event:"ack", stream_id }
 *  5. Frontend manda { event:"media_input", stream_id, media:{ payload: base64(pcm16le 44.1kHz) } }
 *  6. Server manda { event:"media_output", stream_id, media:{ payload: base64(pcm16le 44.1kHz) } }
 *  7. Heartbeat: ogni ~60s manda un evento (es. custom) per evitare idle timeout (180s)
 */
export default function CartesiaCall({
  sessionId,
  companyName,
  gender,
  endpoint, // override endpoint POST (per demo pro). Se non passato usa /api/demo/{sid}/cartesia/session
  endCallEndpoint, // se passato, a fine chiamata POSTiamo qui la trascrizione per estrazione+email
  waPreviewEndpoint, // se passato, durante la chiamata appena c'è 1 contatto POSTiamo qui per WhatsApp anteprima
  onClose,
  onTranscriptUpdate,
}) {
  const [status, setStatus] = useState("connecting"); // connecting | live | ended | error
  const [connStep, setConnStep] = useState(""); // sub-stato visibile durante connecting
  const [errorMsg, setErrorMsg] = useState("");
  const [duration, setDuration] = useState(0);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [liveCaption, setLiveCaption] = useState("");

  const wsRef = useRef(null);
  const streamIdRef = useRef(null);
  const audioCtxRef = useRef(null);
  const micStreamRef = useRef(null);
  const micSourceRef = useRef(null);
  const processorRef = useRef(null);

  // Playback scheduling
  const playbackTimeRef = useRef(0);
  const activeSourcesRef = useRef([]);
  const aiSpeakingTimerRef = useRef(null);

  const timerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const connectTimeoutRef = useRef(null);
  const endedRef = useRef(false);
  const startedRef = useRef(false); // guard contro doppio mount
  const transcriptRef = useRef([]); // [{role, content}] accumulato durante la chiamata
  const waPreviewSentRef = useRef(false); // anteprima WhatsApp inviata una sola volta

  // ============== STT (Cartesia ink-whisper) ==============
  // Cartesia Calls API NON emette transcript via WebSocket: lo facciamo noi aprendo
  // in parallelo 2 STT WebSocket - una per il microfono (user) e una per l'audio agent.
  const sttUserWsRef = useRef(null);
  const sttAgentWsRef = useRef(null);
  // Accumulatori di parole "is_final=false" per costruire frasi complete
  const userPartialRef = useRef("");
  const agentPartialRef = useRef("");

  const SAMPLE_RATE_IN = 44100;   // Cartesia agent audio è pcm_44100
  const SAMPLE_RATE_STT = 16000;  // Cartesia STT consiglia 16kHz pcm_s16le

  // Decima Int16Array da 44100Hz a 16000Hz (lineare semplice, ok per parlato &lt; 4kHz)
  const downsample44to16 = (int16in) => {
    const ratio = SAMPLE_RATE_IN / SAMPLE_RATE_STT;
    const outLen = Math.floor(int16in.length / ratio);
    const out = new Int16Array(outLen);
    for (let i = 0; i < outLen; i++) {
      out[i] = int16in[Math.floor(i * ratio)];
    }
    return out;
  };

  const openSttSocket = (role, accessToken, version) => {
    const params = new URLSearchParams({
      model: "ink-whisper",
      encoding: "pcm_s16le",
      sample_rate: String(SAMPLE_RATE_STT),
      language: "it",
      min_volume: "0.1",
      max_silence_duration_secs: "0.8",
      cartesia_version: version || "2025-04-16",
      access_token: accessToken,
    });
    const url = `wss://api.cartesia.ai/stt/websocket?${params.toString()}`;
    const ws = new WebSocket(url);
    ws.binaryType = "arraybuffer";

    ws.onopen = () => {
      console.log(`[stt ${role}] connected`);
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/_debug/log`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ step: `stt_${role}_open`, msg: "STT WebSocket connesso" }),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    };
    ws.onerror = (e) => {
      console.warn(`[stt ${role}] error`, e);
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/_debug/log`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ step: `stt_${role}_error`, msg: String(e?.message || e).slice(0,200) }),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    };
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        // Log ogni evento STT al backend per debug
        try {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/_debug/log`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ step: `stt_${role}_msg`, msg: `type=${data.type} is_final=${data.is_final} text=${String(data.text||'').slice(0,80)}` }),
            keepalive: true,
          }).catch(() => {});
        } catch {}
        if (data.type === "transcript") {
          const text = (data.text || "").trim();
          if (!text) return;
          if (data.is_final) {
            // Push finale nel transcript (ogni frammento è una riga separata — il backend assembla)
            const entry = { role, content: text, _ts: Date.now() };
            transcriptRef.current.push(entry);
            // Reset partial per quel ruolo
            if (role === "user") userPartialRef.current = "";
            else agentPartialRef.current = "";
            // Aggiorno UI caption
            setLiveCaption(text);
            // Salva in real-time su DB tramite DemoPro (entry singolo {role, content})
            if (typeof onTranscriptUpdate === "function") {
              try { onTranscriptUpdate({ role, content: text }); } catch {}
            }
          } else {
            // partial: aggiorno solo la caption visuale (no push)
            if (role === "user") userPartialRef.current = text;
            else agentPartialRef.current = text;
            setLiveCaption(text);
          }
        } else if (data.type === "error") {
          console.warn(`[stt ${role}] error:`, data);
        }
      } catch {}
    };
    ws.onclose = (e) => {
      console.log(`[stt ${role}] closed code=${e.code} reason=${e.reason}`);
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/_debug/log`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ step: `stt_${role}_close`, msg: `code=${e.code} reason=${e.reason||''}` }),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    };
    return ws;
  };

  const sendInt16ToStt = (ws, int16_44100) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const ds = downsample44to16(int16_44100);
    ws.send(ds.buffer);
  };
  // ============== fine STT ==============

  useEffect(() => {
    start();
    return () => cleanup();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (status === "live") {
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    }
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [status]);

  // Helper debug log → backend + UI sub-step
  const dbg = (step, msg, extra) => {
    // Aggiorna sub-stato visibile nell'UI durante connecting
    const stepLabels = {
      start:           "Inizializzazione…",
      fetch_session:   "Contatto server…",
      fetch_session_ok:"Server OK, apertura audio…",
      audio_ctx_ok:    "Audio pronto, accesso microfono…",
      mic_ok:          "Microfono attivo, connessione Cartesia…",
      ws_create:       "Apertura WebSocket…",
      ws_open:         "WebSocket aperto, avvio sessione…",
      ws_start_sent:   "Sessione avviata, in attesa risposta…",
      ws_msg:          "Risposta ricevuta!",
      connect_timeout: "Timeout connessione",
      ws_error:        "Errore WebSocket",
      ws_close:        "WebSocket chiuso",
    };
    if (stepLabels[step]) setConnStep(stepLabels[step]);
    const backend = process.env.REACT_APP_BACKEND_URL;
    try {
      fetch(`${backend}/api/_debug/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step, msg: String(msg).slice(0, 300), ...extra }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
    console.log(`[24vox ${step}]`, msg, extra || "");
  };

  // ============== START ==============
  const start = async () => {
    // Guard contro StrictMode doppio mount in dev
    if (startedRef.current) return;
    startedRef.current = true;
    dbg("start", "inizio start()");
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const url = endpoint || `${backend}/api/demo/${sessionId}/cartesia/session`;
      dbg("fetch_session", `POST ${url}`);
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gender: gender || "M" }),
      });
      if (!resp.ok) {
        dbg("fetch_session_err", `HTTP ${resp.status}`);
        throw new Error("Impossibile avviare la chiamata. Riprova tra poco.");
      }
      const cfg = await resp.json();
      if (!cfg.token || !cfg.agent_id) {
        dbg("fetch_session_err", "token o agent_id mancante");
        throw new Error("Servizio non disponibile. Riprova tra poco.");
      }
      dbg("fetch_session_ok", `agent_id=${cfg.agent_id} token_len=${cfg.token?.length}`);

      // AudioContext a 44.1kHz con bassa latenza
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) { dbg("audio_ctx_err", "AudioContext non disponibile"); }
      const audioCtx = new AudioCtx({ sampleRate: 44100, latencyHint: "interactive" });
      audioCtxRef.current = audioCtx;
      playbackTimeRef.current = audioCtx.currentTime;
      dbg("audio_ctx_ok", `state=${audioCtx.state}`);

      // Microfono
      dbg("mic", "getUserMedia...");
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      micStreamRef.current = stream;
      dbg("mic_ok", `tracks=${stream.getTracks().length}`);

      // Riprendi AudioContext se sospeso (Chrome richiede gesto utente)
      if (audioCtx.state === "suspended") {
        try { await audioCtx.resume(); } catch {}
      }
      dbg("audio_ctx_resumed", `state=${audioCtx.state}`);

      // WebSocket tramite proxy backend (stesso origin → no CORS/CSP issues)
      const version = cfg.version || "2025-04-16";
      const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
      const wsUrl =
        `${wsProto}//${window.location.host}/api/ws/cartesia` +
        `?token=${encodeURIComponent(cfg.token)}` +
        `&agent_id=${encodeURIComponent(cfg.agent_id)}` +
        `&version=${encodeURIComponent(version)}`;

      dbg("ws_create", `url=${wsProto}//${window.location.host}/api/ws/cartesia...`);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Timeout connessione: se dopo 18 secondi non arriva ack, mostra errore
      connectTimeoutRef.current = setTimeout(() => {
        const wsState = wsRef.current?.readyState;
        dbg("connect_timeout", `ws.readyState=${wsState}`);
        if (!endedRef.current && wsState !== WebSocket.CLOSED) {
          setErrorMsg("Connessione a Cartesia non riuscita. Controlla la tua connessione e riprova.");
          setStatus("error");
          try { wsRef.current?.close(); } catch {}
        }
      }, 18000);

      ws.onopen = () => {
        dbg("ws_open", "WebSocket aperto, invio start event");
        // Apro le 2 WebSocket STT Cartesia (user mic + agent audio)
        try {
          sttUserWsRef.current = openSttSocket("user", cfg.token, version);
          sttAgentWsRef.current = openSttSocket("assistant", cfg.token, version);
        } catch (e) {
          console.warn("[stt] open failed:", e);
          dbg("stt_err", String(e));
        }

        // Evento "start" - formato Calls API ufficiale
        const startEvt = {
          event: "start",
          config: {
            input_format: "pcm_44100",
            voice_id: cfg.voice_id || undefined,
          },
          agent: {
            introduction: cfg.introduction || undefined,
            system_prompt: cfg.system_prompt || undefined,
          },
          metadata: {
            session_id: sessionId,
            company: companyName || "",
          },
        };
        ws.send(JSON.stringify(startEvt));
        dbg("ws_start_sent", `size=${JSON.stringify(startEvt).length}`);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.event !== "media_output") {
            dbg("ws_msg", `event=${msg.event} stream_id=${msg.stream_id||''}`);
          }
          handleEvent(msg);
        } catch (e) {
          // ignore non-JSON
        }
      };

      ws.onerror = (e) => {
        dbg("ws_error", String(e?.message || e));
        setErrorMsg("Errore di connessione. Riprova tra poco.");
        setStatus("error");
      };

      ws.onclose = (e) => {
        dbg("ws_close", `code=${e.code} reason=${e.reason||''} wasClean=${e.wasClean}`);
        if (endedRef.current) return;
        if (e.code === 1000 && /idle/i.test(e.reason || "")) {
          // idle timeout - mostra terminata
          setStatus("ended");
        } else if (status === "connecting" || status === "live") {
          setErrorMsg("Connessione interrotta. Riprova tra poco.");
          setStatus("error");
        }
      };
    } catch (e) {
      setErrorMsg(e.message || "Errore avvio chiamata");
      setStatus("error");
      toast.error("Impossibile avviare la chiamata. Riprova tra poco.");
    }
  };

  // ============== EVENTI IN INGRESSO ==============
  // ============== EVENTI IN INGRESSO ==============
  // Estrae nome/telefono/email dalla trascrizione accumulata lato cliente
  const extractPartialFromTranscript = () => {
    const userText = transcriptRef.current
      .filter((m) => m.role === "user")
      .map((m) => m.content)
      .join(" ");
    // email
    const emailMatch = userText.match(/[\w\.\-+]+@[\w\.\-]+\.\w+/);
    const email = emailMatch ? emailMatch[0] : null;
    // tel: cerco gruppi di 3+ cifre (anche separate da spazi/trattini) lunghezza totale >= 9
    const phoneCandidates = [
      ...userText.matchAll(/(\+?\d[\d\s\-\.\/]{7,}\d)/g),
    ];
    let tel = null;
    for (const c of phoneCandidates) {
      const digits = c[0].replace(/\D/g, "");
      if (digits.length >= 9 && digits.length <= 14) {
        tel = c[0].trim();
        break;
      }
    }
    // nome: pattern "mi chiamo X", "sono X", "il mio nome è X" (best-effort)
    let nome = null;
    const nameRegexes = [
      /mi chiamo\s+([A-ZÀÁÉÈÌÍÒÓÙÚa-zàáéèìíòóùú']+(?:\s+[A-ZÀÁÉÈÌÍÒÓÙÚa-zàáéèìíòóùú']+)?)/i,
      /il mio nome [eè']?\s+([A-ZÀÁÉÈÌÍÒÓÙÚa-zàáéèìíòóùú']+(?:\s+[A-ZÀÁÉÈÌÍÒÓÙÚa-zàáéèìíòóùú']+)?)/i,
      /sono\s+([A-ZÀÁÉÈÌÍÒÓÙÚ][a-zàáéèìíòóùú']+(?:\s+[A-ZÀÁÉÈÌÍÒÓÙÚ][a-zàáéèìíòóùú']+)?)/,
    ];
    for (const re of nameRegexes) {
      const m = userText.match(re);
      if (m) {
        nome = m[1].trim();
        break;
      }
    }
    const motivo = transcriptRef.current
      .filter((m) => m.role === "user")
      .slice(0, 2)
      .map((m) => m.content)
      .join(" · ")
      .slice(0, 120);
    return { nome, tel, email, motivo };
  };

  const maybeSendWaPreview = () => {
    if (waPreviewSentRef.current) return;
    if (!waPreviewEndpoint) return;
    const partial = extractPartialFromTranscript();
    // Manda l'anteprima solo se ho almeno telefono OPPURE email
    if (!partial.tel && !partial.email) return;
    waPreviewSentRef.current = true;
    try {
      fetch(waPreviewEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ partial }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  };

  const handleEvent = (msg) => {
    const ev = msg.event || msg.type || "";

    // DEBUG: inoltra tutti gli eventi (escluso media_output/ack/media_input che sono troppi)
    // a un endpoint backend per analizzare quali nomi-evento Cartesia emette davvero.
    if (ev && ev !== "media_output" && ev !== "media_input" && ev !== "ack") {
      try {
        const safeMsg = JSON.parse(JSON.stringify(msg));
        // Tronco campi binari/troppo lunghi
        if (safeMsg.media?.payload) safeMsg.media.payload = `<b64 ${safeMsg.media.payload.length}>`;
        if (safeMsg.data && String(safeMsg.data).length > 200) safeMsg.data = `<truncated ${String(safeMsg.data).length}>`;
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/_debug/cartesia-event`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, event_data: safeMsg, event: ev }),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    }

    if (ev === "ack") {
      if (connectTimeoutRef.current) { clearTimeout(connectTimeoutRef.current); connectTimeoutRef.current = null; }
      streamIdRef.current = msg.stream_id;
      setStatus("live");
      // Avvia cattura microfono dopo ack
      setupMicCapture();
      // Heartbeat ogni 60s per evitare idle timeout (180s)
      heartbeatRef.current = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          try {
            wsRef.current.send(
              JSON.stringify({
                event: "custom",
                stream_id: streamIdRef.current,
                metadata: { ping: Date.now() },
              })
            );
          } catch {}
        }
      }, 60000);
      return;
    }

    if (ev === "media_output") {
      const b64 = msg.media?.payload || msg.data || msg.audio || "";
      if (b64) schedulePlayback(b64);
      return;
    }

    if (ev === "clear" || ev === "interrupt") {
      stopAllPlayback();
      setAiSpeaking(false);
      setLiveCaption("");
      return;
    }

    // Trascrizioni — gestiamo TUTTI i nomi di evento possibili che Cartesia usa
    // Documentazione: transcript / agent_transcript / user_transcript / tts.text / asr.* / message
    const evLow = String(ev).toLowerCase();
    const isUserTranscript =
      evLow === "user_transcript" ||
      evLow === "user.transcript" ||
      evLow === "asr.final" ||
      evLow === "asr.partial" ||
      evLow === "transcription.user" ||
      evLow === "user_message" ||
      evLow === "user.message" ||
      (evLow === "transcript" && (msg.role === "user" || msg.speaker === "user"));
    const isAgentTranscript =
      evLow === "agent_transcript" ||
      evLow === "agent.transcript" ||
      evLow === "tts.text" ||
      evLow === "transcription.agent" ||
      evLow === "agent_message" ||
      evLow === "agent.message" ||
      evLow === "assistant_message" ||
      (evLow === "transcript" && (msg.role === "agent" || msg.role === "assistant" || msg.speaker === "agent" || msg.speaker === "assistant"));

    if (isUserTranscript) {
      const text = (msg.text || msg.transcript || msg.content || msg.message || msg.utterance || "").trim();
      if (text) {
        transcriptRef.current.push({ role: "user", content: text });
        saveCartesiaTranscript(sessionId, "user", text).catch(() => {});
        onTranscriptUpdate?.({ role: "user", content: text });
        maybeSendWaPreview();
      }
      return;
    }
    if (isAgentTranscript) {
      const text = (msg.text || msg.transcript || msg.content || msg.message || msg.utterance || "").trim();
      if (text) {
        setLiveCaption(text);
        transcriptRef.current.push({ role: "assistant", content: text });
        saveCartesiaTranscript(sessionId, "assistant", text).catch(() => {});
        onTranscriptUpdate?.({ role: "assistant", content: text });
      }
      return;
    }

    // Log diagnostico per ogni evento sconosciuto: serve a capire quali eventi
    // Cartesia sta effettivamente emettendo per questo agent.
    if (ev !== "media_output" && ev !== "ack" && ev !== "media_input") {
      console.log("[cartesia event]", ev, JSON.stringify(msg).slice(0, 300));
    }

    if (ev === "error") {
      setErrorMsg("Errore durante la chiamata. Riprova.");
      toast.error("Errore durante la chiamata.");
      return;
    }
  };

  // ============== MIC CAPTURE ==============
  const setupMicCapture = () => {
    const audioCtx = audioCtxRef.current;
    const stream = micStreamRef.current;
    const ws = wsRef.current;
    if (!audioCtx || !stream || !ws) return;

    const source = audioCtx.createMediaStreamSource(stream);
    micSourceRef.current = source;

    // Buffer piccolo per latenza minima (1024 sample a 44.1kHz ≈ 23ms)
    const bufferSize = 1024;
    const processor = audioCtx.createScriptProcessor(bufferSize, 1, 1);
    processorRef.current = processor;

    const speakingThreshold = 0.015;

    processor.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN || !streamIdRef.current) return;
      const input = e.inputBuffer.getChannelData(0);

      // VAD locale per UI
      let sum = 0;
      for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
      const rms = Math.sqrt(sum / input.length);
      setUserSpeaking(rms > speakingThreshold);

      // Float32 → Int16 PCM little-endian
      const pcm = new Int16Array(input.length);
      for (let i = 0; i < input.length; i++) {
        let s = Math.max(-1, Math.min(1, input[i]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }

      // Mando in parallelo: (1) all'agent Cartesia per la risposta vocale,
      // (2) alla STT Cartesia per la trascrizione lato utente.
      sendInt16ToStt(sttUserWsRef.current, pcm);

      const b64 = arrayBufferToBase64(pcm.buffer);
      ws.send(
        JSON.stringify({
          event: "media_input",
          stream_id: streamIdRef.current,
          media: { payload: b64 },
        })
      );
    };

    source.connect(processor);
    processor.connect(audioCtx.destination);
  };

  // ============== PLAYBACK ==============
  const schedulePlayback = (b64) => {
    const audioCtx = audioCtxRef.current;
    if (!audioCtx) return;

    const ab = base64ToArrayBuffer(b64);
    const pcm = new Int16Array(ab);
    if (pcm.length === 0) return;

    // Inoltro alla STT per la voce dell'agent (per ottenere il transcript dell'AI)
    sendInt16ToStt(sttAgentWsRef.current, pcm);

    const float = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) float[i] = pcm[i] / 0x8000;

    const buf = audioCtx.createBuffer(1, float.length, 44100);
    buf.getChannelData(0).set(float);

    const src = audioCtx.createBufferSource();
    src.buffer = buf;

    const gainNode = audioCtx.createGain();
    gainNode.gain.value = 1.4;

    const compressor = audioCtx.createDynamicsCompressor();
    compressor.threshold.value = -18;
    compressor.knee.value = 8;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    src.connect(gainNode);
    gainNode.connect(compressor);
    compressor.connect(audioCtx.destination);

    const now = audioCtx.currentTime;
    const startAt = Math.max(playbackTimeRef.current, now);
    src.start(startAt);
    playbackTimeRef.current = startAt + buf.duration;

    setAiSpeaking(true);
    if (aiSpeakingTimerRef.current) clearTimeout(aiSpeakingTimerRef.current);
    aiSpeakingTimerRef.current = setTimeout(() => setAiSpeaking(false), Math.max(300, buf.duration * 1000 + 200));

    activeSourcesRef.current.push(src);
    src.onended = () => {
      activeSourcesRef.current = activeSourcesRef.current.filter((s) => s !== src);
    };
  };

  const stopAllPlayback = () => {
    activeSourcesRef.current.forEach((s) => {
      try {
        s.stop();
      } catch {}
    });
    activeSourcesRef.current = [];
    if (audioCtxRef.current) {
      playbackTimeRef.current = audioCtxRef.current.currentTime;
    }
  };

  // ============== CLEANUP ==============
  const cleanedRef = useRef(false);
  const cleanup = () => {
    if (cleanedRef.current) return; // idempotente: chiamato sia da handleEnd che da unmount
    cleanedRef.current = true;
    endedRef.current = true;

    // Prima cosa: chiedo alle 2 STT di finalizzare gli ultimi transcript pendenti.
    try {
      const u = sttUserWsRef.current;
      if (u && u.readyState === WebSocket.OPEN) u.send("finalize");
      const a = sttAgentWsRef.current;
      if (a && a.readyState === WebSocket.OPEN) a.send("finalize");
    } catch {}

    const buildMsgs = () =>
      transcriptRef.current.length > 0
        ? transcriptRef.current.map(({ role, content }) => ({ role, content }))
        : [{ role: "assistant", content: "(Chiamata avvenuta. Nessuna trascrizione disponibile.)" }];

    const sendFinalNow = () => {
      // Invio IMMEDIATO con sendBeacon — sopravvive alla chiusura pagina/tab
      if (!endCallEndpoint) return;
      try {
        const body = JSON.stringify({ messages: buildMsgs() });
        const blob = new Blob([body], { type: "application/json" });
        if (navigator.sendBeacon) navigator.sendBeacon(endCallEndpoint, blob);
      } catch {}
    };

    const sendFinalFetch = () => {
      // Invio con fetch dopo 700ms — per raccogliere gli ultimi messaggi STT
      if (!endCallEndpoint) return;
      try {
        const body = JSON.stringify({ messages: buildMsgs() });
        fetch(endCallEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          keepalive: true,
        }).catch(() => {});
      } catch {}
    };

    // Invio subito (per page-unload) + di nuovo dopo 1500ms (per catturare gli ultimi STT).
    // Il backend è idempotente: riconosce invii doppi e invia il WA una sola volta.
    sendFinalNow();
    setTimeout(sendFinalFetch, 1500);
    try {
      stopAllPlayback();
    } catch {}
    // Chiudo le 2 STT WebSocket Cartesia in modo pulito (dopo aver dato tempo a finalize+sendFinal)
    setTimeout(() => {
      try {
        const u = sttUserWsRef.current;
        if (u && u.readyState === WebSocket.OPEN) { u.send("done"); setTimeout(() => { try { u.close(); } catch {} }, 100); }
        const a = sttAgentWsRef.current;
        if (a && a.readyState === WebSocket.OPEN) { a.send("done"); setTimeout(() => { try { a.close(); } catch {} }, 100); }
      } catch {}
    }, 1100);
    try {
      if (connectTimeoutRef.current) clearTimeout(connectTimeoutRef.current);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    } catch {}
    try {
      if (aiSpeakingTimerRef.current) clearTimeout(aiSpeakingTimerRef.current);
    } catch {}
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            event: "end",
            stream_id: streamIdRef.current,
          })
        );
      }
    } catch {}
    try {
      wsRef.current?.close(1000, "user ended");
    } catch {}
    try {
      processorRef.current?.disconnect();
    } catch {}
    try {
      micSourceRef.current?.disconnect();
    } catch {}
    try {
      micStreamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {}
    try {
      // Chiudi AudioContext solo se non è già chiuso
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state !== "closed") {
        ctx.close().catch(() => {});
      }
    } catch {}
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleEnd = () => {
    cleanup();
    setStatus("ended");
    onClose?.();
  };

  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div
      data-testid="cartesia-call-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/85 backdrop-blur-md p-4"
    >
      <div className="w-full max-w-md bg-brand-bg rounded-3xl shadow-2xl border border-brand-line p-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <span
            className={`w-2 h-2 rounded-full ${
              status === "live"
                ? "bg-green-500 animate-pulse"
                : status === "connecting"
                ? "bg-amber-500 animate-pulse"
                : "bg-red-500"
            }`}
          />
          <span
            className="text-xs uppercase tracking-widest text-brand-inkSoft font-medium"
            data-testid="call-status"
          >
            {status === "connecting" && "in connessione..."}
            {status === "live" && `in chiamata · ${fmtTime(duration)}`}
            {status === "ended" && "chiamata terminata"}
            {status === "error" && "errore"}
          </span>
          {status === "connecting" && connStep && (
            <span className="text-xs text-brand-inkSoft/70 ml-2">{connStep}</span>
          )}
        </div>

        <div className="relative w-40 h-40 mx-auto mb-6">
          <div
            className={`absolute inset-0 rounded-full bg-brand-terra/20 ${
              aiSpeaking ? "animate-ping" : ""
            }`}
          />
          <div
            className={`absolute inset-3 rounded-full bg-brand-terra/30 ${
              aiSpeaking ? "animate-pulse" : ""
            }`}
          />
          <div className="absolute inset-6 rounded-full bg-brand-terra flex items-center justify-center">
            {status === "connecting" ? (
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            ) : (
              <Mic
                className={`w-10 h-10 text-white ${
                  userSpeaking ? "scale-110" : ""
                } transition-transform`}
              />
            )}
          </div>
        </div>

        <h2 className="font-heading text-2xl text-brand-ink mb-1" data-testid="call-company-name">
          {companyName}
        </h2>
        <p className="text-sm text-brand-inkSoft mb-6">demo 24Vox · voce reale</p>

        {liveCaption && (
          <div
            className="bg-white/60 rounded-xl px-4 py-3 mb-6 text-sm text-brand-ink italic min-h-[3rem]"
            data-testid="live-caption"
          >
            "{liveCaption}"
          </div>
        )}

        {status === "error" && (
          <div
            className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6 text-sm text-red-800"
            data-testid="call-error"
          >
            {errorMsg}
          </div>
        )}

        {status === "live" && (
          <p className="text-xs text-brand-inkSoft mb-6">
            {userSpeaking
              ? "Ti sto ascoltando..."
              : aiSpeaking
              ? "Parla l'assistente..."
              : "Parla pure, come al telefono"}
          </p>
        )}

        <button
          onClick={handleEnd}
          className="w-16 h-16 mx-auto rounded-full bg-red-500 hover:bg-red-600 transition flex items-center justify-center text-white shadow-lg"
          data-testid="end-cartesia-call-button"
          aria-label="Termina chiamata"
        >
          <PhoneOff className="w-7 h-7" />
        </button>
      </div>
    </div>
  );
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function base64ToArrayBuffer(b64) {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}
